import { useEffect } from 'react';
import axios from 'axios';

export const THEMES = {
  amber: {
    '50': '#fffbeb', '100': '#fef3c7', '200': '#fde68a', '300': '#fcd34d', '400': '#fbbf24',
    '500': '#f59e0b', '600': '#d97706', '700': '#b45309', '800': '#92400e', '900': '#78350f', '950': '#451a03'
  },
  emerald: {
    '50': '#ecfdf5', '100': '#d1fae5', '200': '#a7f3d0', '300': '#6ee7b7', '400': '#34d399',
    '500': '#10b981', '600': '#059669', '700': '#047857', '800': '#065f46', '900': '#064e3b', '950': '#022c22'
  },
  blue: {
    '50': '#eff6ff', '100': '#dbeafe', '200': '#bfdbfe', '300': '#93c5fd', '400': '#60a5fa',
    '500': '#3b82f6', '600': '#2563eb', '700': '#1d4ed8', '800': '#1e40af', '900': '#1e3a8a', '950': '#172554'
  },
  rose: {
    '50': '#fff1f2', '100': '#ffe4e6', '200': '#fecdd3', '300': '#fda4af', '400': '#fb7185',
    '500': '#f43f5e', '600': '#e11d48', '700': '#be123c', '800': '#9f1239', '900': '#881337', '950': '#4c0519'
  },
  stone: {
    '50': '#fafaf9', '100': '#f5f5f4', '200': '#e7e5e4', '300': '#d6d3d1', '400': '#a8a29e',
    '500': '#78716c', '600': '#57534e', '700': '#44403c', '800': '#292524', '900': '#1c1917', '950': '#0c0a09'
  },
  indigo: {
    '50': '#e0e7ff', '100': '#c7d2fe', '200': '#a5b4fc', '300': '#818cf8', '400': '#6366f1',
    '500': '#4f46e5', '600': '#4338ca', '700': '#3730a3', '800': '#2b2a6b', '900': '#1e1b4b', '950': '#0f0e26'
  }
} as const;

export const FONTS = {
  'sans-serif': 'system-ui, -apple-system, sans-serif',
  'Inter': "'Inter', system-ui, -apple-system, sans-serif",
  'Outfit': "'Outfit', system-ui, -apple-system, sans-serif",
  'Playfair Display': "'Playfair Display', Georgia, serif",
  'Nanum Gothic': "'Nanum Gothic', sans-serif",
  'Nanum Myeongjo': "'Nanum Myeongjo', serif"
} as const;

// 폰트별 Google Fonts 링크 로드 함수
const loadGoogleFont = (fontName: string) => {
  if (fontName === 'sans-serif' || fontName.startsWith('Nanum')) return; // 한글 폰트는 기본 로컬이 아닐 시 다른 방식으로 로드 가능
  
  const id = `gfont-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@300;400;500;700;800&display=swap`;
  document.head.appendChild(link);
};

export const applyThemeSettings = (themeName: keyof typeof THEMES, fontName: keyof typeof FONTS) => {
  const root = document.documentElement;

  // 1. 테마 색상 적용
  const colors = THEMES[themeName] || THEMES.amber;
  Object.entries(colors).forEach(([shade, value]) => {
    root.style.setProperty(`--color-primary-${shade}`, value);
  });

  // 2. 폰트 적용 및 구글 폰트 로드
  const fontValue = FONTS[fontName] || FONTS['sans-serif'];
  root.style.setProperty('--font-sans', fontValue);

  if (fontName === 'Inter' || fontName === 'Outfit' || fontName === 'Playfair Display') {
    loadGoogleFont(fontName);
  } else if (fontName.startsWith('Nanum')) {
    // 나눔 폰트 CDN 로드
    const family = fontName.replace(/\s+/g, '').toLowerCase();
    const id = `gfont-${family}`;
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}&display=swap`;
      document.head.appendChild(link);
    }
  }
};

export default function ThemeSync() {
  useEffect(() => {
    // 1. 로컬 스토리지에 캐시된 테마/폰트 즉시 적용 (FOUC 방지)
    const cachedTheme = localStorage.getItem('site_theme_color') as keyof typeof THEMES | null;
    const cachedFont = localStorage.getItem('site_font_family') as keyof typeof FONTS | null;
    if (cachedTheme && cachedFont) {
      applyThemeSettings(cachedTheme, cachedFont);
    } else {
      // 캐시가 없으면 앰버/기본 폰트 적용
      applyThemeSettings('amber', 'sans-serif');
    }

    // 2. 백엔드에서 실시간 설정 조회 후 갱신
    axios.get('/api/settings')
      .then(res => {
        const { theme_color, font_family, brand_name } = res.data;
        if (theme_color && font_family) {
          localStorage.setItem('site_theme_color', theme_color);
          localStorage.setItem('site_font_family', font_family);
          applyThemeSettings(theme_color, font_family);
        }
        if (brand_name) {
          localStorage.setItem('site_brand_name', brand_name);
          // dispatch a custom event so other components know the brand name has been updated
          window.dispatchEvent(new Event('site_settings_updated'));
        }
      })
      .catch(() => {
        // 백엔드 통신 실패 시 캐시 유지
      });
  }, []);

  return null;
}
